import { Grid, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LwFormSelect, LwFormTime, StatusIndicator } from 'redesign';
import { areDateStringsEqual, compareDateStrings } from 'shared/utils/validate-date';
import { MultiDayDayBlockTimeInputsProps } from './multi-day-day-block-time-inputs.types';
import { getMultiDayDayBlockFieldPath } from '../helpers/multi-day-day-block.helpers';
import { isOvernightTimeRange } from '../helpers/is-overnight-time-range';
import { MultiDayWorkFormContext } from '../multi-day-work.types';

export const MultiDayDayBlockTimeInputs = ({
  breakOptions,
  index,
}: MultiDayDayBlockTimeInputsProps) => {
  const { t } = useTranslation('web-common');
  const theme = useTheme();
  const { control, watch } = useFormContext<MultiDayWorkFormContext>();

  const startTimeFieldPath = getMultiDayDayBlockFieldPath(index, 'startTime');
  const endTimeFieldPath = getMultiDayDayBlockFieldPath(index, 'endTime');
  const breakMinutesFieldPath = getMultiDayDayBlockFieldPath(index, 'breakMinutes');

  const watchedStartTime = watch(startTimeFieldPath) ?? '';
  const watchedEndTime = watch(endTimeFieldPath) ?? '';

  const endTimeMin =
    watchedStartTime && !isOvernightTimeRange(watchedStartTime, watchedEndTime)
      ? watchedStartTime
      : '00:00';
  const showNightShiftIndicator = isOvernightTimeRange(watchedStartTime, watchedEndTime);

  return (
    <Grid columnSpacing={4} container rowSpacing={4}>
      <Grid item xs={6}>
        <LwFormTime
          name={startTimeFieldPath}
          label={t('work_form.multi_day.day_blocks.start_time.label')}
          control={control}
          min="00:00"
          max="23:59"
          rules={{
            required: t('work_form.multi_day.day_blocks.start_time.required'),
            validate: {
              isAfterMinTime: (value) =>
                value !== undefined &&
                (compareDateStrings('00:00', value.toString()) ||
                  areDateStringsEqual('00:00', value.toString()) ||
                  t('work_form.multi_day.day_blocks.start_time.after_min', { min: '00:00' })),
            },
          }}
          data-testid={`multi-day-day-block-${index}-start-time`}
        />
      </Grid>
      <Grid item xs={6}>
        <LwFormTime
          name={endTimeFieldPath}
          label={t('work_form.multi_day.day_blocks.end_time.label')}
          control={control}
          min={endTimeMin}
          rules={{
            required: t('work_form.multi_day.day_blocks.end_time.required'),
            validate: {
              isAfterMinTime: (value) =>
                value !== undefined &&
                (compareDateStrings(endTimeMin, value.toString()) ||
                  areDateStringsEqual(endTimeMin, value.toString()) ||
                  t('work_form.multi_day.day_blocks.end_time.after_min', { min: endTimeMin })),
            },
          }}
          data-testid={`multi-day-day-block-${index}-end-time`}
          InputProps={{
            endAdornment: showNightShiftIndicator ? (
              <InputAdornment position="end">
                <StatusIndicator
                  backgroundColor={theme.palette.lwPrimaryAccentLight[10]}
                  color={theme.palette.lwPrimaryAccentDark[100]}
                  label={t('work_form.multi_day.day_blocks.night_shift_indicator')}
                />
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <LwFormSelect
          name={breakMinutesFieldPath}
          label={t('work_form.multi_day.day_blocks.break.label')}
          defaultLabel={t('work_form.multi_day.day_blocks.break.default_label')}
          control={control}
          options={breakOptions}
          rules={{
            required: t('work_form.multi_day.day_blocks.break.required'),
          }}
          data-testid={`multi-day-day-block-${index}-break`}
        />
      </Grid>
    </Grid>
  );
};
